// WebRTC peer for the TV (answerer).
// Phone is the offerer: creates the RTCPeerConnection, creates the data channel,
// sends the offer. TV just answers and accepts the incoming data channel.

import { isPhoneMessage, type PhoneToTvMessage, type TvToPhoneMessage } from './messages';

const STUN_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export type PeerConnectionState = 'idle' | 'connecting' | 'connected' | 'closed' | 'failed';

export interface TvPeerEvents {
  onConnectionState?: (state: PeerConnectionState) => void;
  onMessage?: (msg: PhoneToTvMessage) => void;
  onLocalIceCandidate?: (candidate: RTCIceCandidateInit) => void;
  onLocalAnswer?: (answer: RTCSessionDescriptionInit) => void;
}

export class TvPeer {
  private pc: RTCPeerConnection;
  private dc: RTCDataChannel | null = null;
  private events: TvPeerEvents;
  private state: PeerConnectionState = 'idle';
  private closed = false;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private remoteDescSet = false;

  constructor(events: TvPeerEvents) {
    this.events = events;
    this.pc = new RTCPeerConnection({ iceServers: STUN_SERVERS });
    this.setState('connecting');

    this.pc.onicecandidate = (e) => {
      if (e.candidate) this.events.onLocalIceCandidate?.(e.candidate.toJSON());
    };

    this.pc.onconnectionstatechange = () => {
      const s = this.pc.connectionState;
      // Promote to 'connected' only on dc.onopen (below). pc.connectionState
      // can flip to 'connected' before the SCTP data channel is actually
      // ready, so messages sent in that window get dropped.
      if (s === 'failed') this.setState('failed');
      else if (s === 'closed' || s === 'disconnected') this.setState('closed');
    };

    // Phone creates the data channel; TV receives it here
    this.pc.ondatachannel = (e) => {
      this.dc = e.channel;
      this.dc.binaryType = 'arraybuffer';
      this.dc.onopen = () => this.setState('connected');
      this.dc.onclose = () => this.setState('closed');
      this.dc.onmessage = (ev) => this.handleMessage(ev.data);
    };
  }

  private handleMessage(raw: unknown): void {
    try {
      const parsed = JSON.parse(String(raw));
      if (!isPhoneMessage(parsed)) return;
      this.events.onMessage?.(parsed);
    } catch {
      // ignore malformed messages
    }
  }

  private setState(state: PeerConnectionState): void {
    if (this.state === state || this.closed) return;
    this.state = state;
    this.events.onConnectionState?.(state);
  }

  async acceptOffer(offer: RTCSessionDescriptionInit): Promise<void> {
    if (this.closed) return;
    await this.pc.setRemoteDescription(offer);
    this.remoteDescSet = true;
    // Flush any ICE candidates that arrived before the offer
    for (const c of this.pendingCandidates) {
      await this.pc.addIceCandidate(c).catch(() => {});
    }
    this.pendingCandidates = [];
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    if (this.pc.localDescription) {
      this.events.onLocalAnswer?.(this.pc.localDescription.toJSON());
    }
  }

  async addRemoteIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (this.closed) return;
    if (!this.remoteDescSet) {
      this.pendingCandidates.push(candidate);
      return;
    }
    try {
      await this.pc.addIceCandidate(candidate);
    } catch {
      // Benign — browser sometimes rejects duplicates
    }
  }

  send(msg: TvToPhoneMessage): boolean {
    if (!this.dc || this.dc.readyState !== 'open') return false;
    try {
      this.dc.send(JSON.stringify(msg));
      return true;
    } catch {
      return false;
    }
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    try { this.dc?.close(); } catch {}
    try { this.pc.close(); } catch {}
    this.setState('closed');
  }
}
